import {
  GridColumn as Column,
  GridCellProps,
} from '@progress/kendo-react-grid';
import { LightPageHeader } from 'Components/Display';
import {
  DateCell,
  EditDeleteNotesCell,
  FilterBar,
  FilteredMainGrid,
  LinkCell,
} from 'Components/Grid';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import { ColumnMenu } from 'Components/Grid/TableHeader/ColumnMenu';
import {
  FilterBarProvider,
  FormProvider,
  GridProvider,
  useEnums,
} from 'Context';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  IFilterConfig,
} from 'Models';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { ReferralSubRoute, ROUTES } from 'Navigation';
import React from 'react';
import { Col, Row } from 'reactstrap';
import { buildEndpoint, useGridSearch } from 'Utilities';

class AssignedToWriterSearch {
  searchKey?: string;
  id?: string;
  client?: string;
  benefitState?: string;
  appointmentDate?: Date;
}

export enum AssignedToWritersGridIds {
  DetailedSearch = 'detailedSearch',
}

export const AssignedToWriters: React.FC = () => {
  return (
    <GridProvider entityName="Referral">
      <LightPageHeader tall noBorder className="page-header-shadow">
        <Row>
          <Col>
            <h1>Under Review Queue</h1>
          </Col>
        </Row>
      </LightPageHeader>
      <br />
      <Row>
        <Col></Col>
      </Row>
      <FilterBarDetailedSearchGrid />
    </GridProvider>
  );
};

const FilterBarDetailedSearchGrid: React.FC = () => {
  const assignedToWritersSearch = new AssignedToWriterSearch();

  return (
    <FormProvider formModel={assignedToWritersSearch}>
      <AssignedToWriterssGrid />
    </FormProvider>
  );
};

const AssignedToWriterssGrid: React.FC = () => {
  const [gridToggler] = useGridSearch();
  const { state, appointmentStatus } = useEnums();

  const filters: IFilterConfig[] = [
    new FilterConfig(
      'searchKey',
      FilterBarFilterType.SearchText,
      'Stradix Number, Claim Number, Claimant, Referral Number, Doctor'
    ),
    new FilterConfig('client', FilterBarFilterType.AsyncDropdown, 'Client', {
      readEndpoint: ENDPOINTS.clients.dropdownItems,
    }),
    new FilterConfig(
      'professional',
      FilterBarFilterType.AsyncDropdown,
      'Professional',
      {
        readEndpoint: buildEndpoint(
          ENDPOINTS.users.endpointString('userByRolDropdownItems'),
          'Professional'
        ),
      }
    ),
    new FilterConfig(
      'appointmentStatus',
      FilterBarFilterType.EnumDropdown,
      'Appointment Status',
      { data: appointmentStatus }
    ),
    new FilterConfig(
      'benefitState',
      FilterBarFilterType.EnumDropdown,
      'State of Jurisdiction',
      { data: state }
    ),
    new FilterConfig(
      'TypeOfEval',
      FilterBarFilterType.AsyncDropdown,
      'Type Of Eval',
      {
        readEndpoint: buildEndpoint(
          ENDPOINTS.clients.endpointString('typeOfEvalDropdownItems')
        ),
      }
    ),
    new FilterConfig(
      'appointmentDate',
      FilterBarFilterType.DatePicker,
      'Appointment Date'
    ),
  ];

  return (
    <React.Fragment>
      <FilterBarProvider filterValues={[]} filterConfigurations={filters}>
        <FilterBar />
        <FilteredMainGrid
          headerDisabled
          isMainGrid
          refreshToggle={gridToggler}
          readEndpoint={ENDPOINTS.assignedtowriters.read}
          enableGridRefresh={true}
          {...defaultGridProps}
        >
          <Column
            field="stradixNumber"
            title="Stradix Number"
            width={'180px'}
            locked={true}
          />
          <Column
            field="claimant"
            title="Claimant Name"
            cell={claimantCell}
            width={'180px'}
          />
          <Column field="claimNumber" title="Claim Number" width={'150px'} />
          <Column field="client" title="Client" width={'150px'} />
          <Column field="doctor" title="Doctor" width={'150px'} />
          <Column
            field="appointmentDate"
            title="Appointment Date"
            cell={DateFormatCell}
            width={'180px'}
          />
          <Column
            field="dateOfBirth"
            title="Date Of Birth"
            cell={DateFormatCell}
            width={'150px'}
          />
          <Column
            field="appointmentStatus"
            title="Appointment Status"
            width={'200px'}
          />
          <Column
            field="benefitState"
            title="State of Jurisdiction"
            width={'180px'}
          />
          <Column field="typeOfEval" title="Type of Eval" width={'180px'} />
          <Column field="notes" title="Notes" width={'150px'} />
          <Column
            field="referralSource"
            title="Referral Source"
            width={'180px'}
          />
          <Column field="writer" title="Writer" width={'150px'} />
          <Column
            field="ownerName"
            title="Owner"
            width={'160px'}
            filterCell={DebouncedTextFilterCell}
            columnMenu={ColumnMenu}
          />
          <Column
            field="id"
            width="120"
            title="Actions"
            cell={ActionsCellEdit}
          />
        </FilteredMainGrid>
      </FilterBarProvider>
    </React.Fragment>
  );
};

const claimantCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.REFERRAL}
      linkField="referralId"
      subRoute={ReferralSubRoute.View}
      {...props}
    ></LinkCell>
  );
};

const DateFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={'MM/DD/YYYY'} />;
};

const ActionsCellEdit: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <EditDeleteNotesCell
      endpoint=""
      editRoute={ROUTES.REFERRAL}
      subRoute={ReferralSubRoute.View}
      linkField="referralId"
      editPermissions={[Permission.EditReferral]}
      deleteEndpoint={ENDPOINTS.referrals.base}
      deletePermissions={[Permission.DeleteReferrals]}
      notesRoute={ROUTES.REFERRAL}
      notesSubRoute={ReferralSubRoute.Notes}
      notesPermissions={[Permission.ViewReferral]}
      {...props}
    />
  );
};
